import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { renderToStaticMarkup } from 'react-dom/server';
import Form from '../../commonComponents/forms/Form';
import translate from '../../commonComponents/translate';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import userProp from '../../commonComponents/propTypes/userProp';
import i18n from '../../services/i18n';
import List from '../../commonComponents/lists/ClientSideList';
import {
  findCurrencySymbol,
  formatPrice,
  handleBillingAddressInOrder,
  handleShippingAddressInOrder,
} from '../../services/utilityFunctions';
import DisplayShippingContainer from './Components/DisplayShippingContainer/DisplayShippingContainer';
import Notifications from '../../services/notifications';
import CurrencyProp from '../profile/propTypes/currencyProp';
import { useModal } from '../../services/customHooks';
import RemoveProductVariationModal from './Components/RemoveProductVariationModal';
import Loader from '../../services/loader';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import AddAddressModal from '../sessions/modal/AddAddressModal';
import VatTypesProp from '../../commonComponents/forms/Address/VatTypesProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import { PAYMENT_PROVIDER } from '../../commonComponents/propTypes/tenantProp';
import ConfigsContext from '../../contextProviders/ConfigsContext';
import CustomerSection from './Components/CustomerSection';
import OperatorSection from './Components/OperatorSection';
import CategoryProp from '../academy/propTypes/categoryProp';
import WarningSection from './Components/modal/components/WarningSection';
import BasicModal from '../../commonComponents/modal/BasicModal';
import AddNewProductModal from './Components/modal/AddNewProductModal';
import {
  getInitialOperator,
  getInitialProductState,
  getInitialShipping,
  getInitialState,
  handleValidationFrequencies,
  MODE,
  getProductsForOrderCreate,
} from './Components/helpers/orderHelper';
import FormModal from '../../commonComponents/modal/FormModal';
import FormButton from '../../commonComponents/buttons/FormButton';
import User from '../../../../../domain/models/user';
import { validateAddressFields } from '../../commonComponents/helpers/addressHelper';
import { normalizeNumber } from '../../services/numberUtilis';
import { PROVIDER_TYPE } from '../../commonComponents/propTypes/marketProductForm';
import PdfFormModal from '../../commonComponents/modal/PdfFormModal';
import OrderCreateFooter from './Components/OrderCreateFooter';
import DocumentsTab from '../shop/detail/components/DocumentsTab';
import RemoveDocumentModal from '../../commonComponents/modal/RemoveDocumentModal';
import { OPPORTUNITIES_STATUS } from '../opportunities/components/helpers/opportunitiesListHelper';
import OrderProp from '../shop/propTypes/orderProp';
import ProductItem from './Components/ProductItem';
import AddPackProduct from '../shop/shopItems/pack/advancedPackComponents/AddPackProduct';

const fetchUser = async userId => {
  try {
    const { data: response } = await axios.get(`/api/user/${userId}`, {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Organization',
      },
    });
    return response;
  } catch (err) {
    console.log('failed to fetch user: ', err);
    return null;
  }
};

const getUserTypeForSelector = () => {
  const userTypeNames = {};
  Object.values(userProp.TYPES).forEach(userType => {
    userTypeNames[userType] = i18n.__(`user_type_${userType}`);
  });
  return userTypeNames;
};

const notValidProductForms = products => {
  const formNames = [];
  products.forEach(product => {
    if (product?.form && !product.formAnswerData) {
      formNames.push(product?.form?.name);
    }
  });

  return formNames?.length > 0 ? formNames?.join('. ')?.slice(0, -2) : null;
};

const initialTotalsState = {
  total: 0,
  subtotal: 0,
  shippingTotal: 0,
  shippingTax: 0,
  discount: 0,
  userDiscount: 0,
  userDiscountPercentage: 0,
  tax: 0,
  currency: 'eur',
};

function OrderCreate({
  t,
  owner,
  currencies,
  countries,
  vatTypes,
  _csrf,
  customer,
  categories,
  catalogTypes,
  isOpportunity,
  languages,
  opportunity,
  mode,
}) {
  /** Select required */
  const userTypeNames = getUserTypeForSelector();
  const [productsSelector, setProductsSelector] = useState([]);
  const [products, setProducts] = useState([]);
  const { loggedUser } = useContext(LoggedUserContext);
  const isEdit = mode === MODE.EDIT;
  const { tenant } = useContext(ConfigsContext);
  const { directSellingSettings, directSelling } = tenant?.settings;
  const { CVIntegration, VAIntegration } = directSellingSettings || {};
  const isOwnerACollaborator =
    loggedUser?.type === User.TYPE.COLLABORATOR ||
    loggedUser?.role === User.ROLE.ADMIN;

  const showVA = directSelling && VAIntegration && isOwnerACollaborator;
  const showCV = directSelling && CVIntegration && isOwnerACollaborator;

  const { paymentProviderType } = tenant.settings || {};

  // Get different information when opportunity or order
  const pageInformation = useMemo(() => {
    const opportunityEndpoint = isEdit
      ? `/api/market/opportunity/${opportunity?._id}?`
      : '/api/market/opportunity?';
    const confirmButtonOpportunity =
      mode === MODE.CREATE ? t('place.offer') : t('edit.offer');
    const titleOpportunity =
      mode === MODE.CREATE ? t('opportunity.create') : t('edit.offer');
    return {
      title: !isOpportunity ? t('order.create') : titleOpportunity,
      confirmButton: !isOpportunity
        ? t('order_place_order')
        : confirmButtonOpportunity,
      confirmClass: !isOpportunity ? 'btn btn-dynamic' : 'btn btn-info',
      calculateEndpoint: !isOpportunity
        ? '/api/market/order/calculate?populate=products.product'
        : '/api/market/opportunity/calculate?populate=products.product',
      createEndpoint: !isOpportunity
        ? '/api/market/order?'
        : opportunityEndpoint,
      goBackUrl: !isOpportunity ? '/shop/order' : '/shop/opportunities',
    };
  }, [isOpportunity, t, opportunity, mode]);

  /** Init Variables */
  const formId = 'createOrder';
  const tableId = 'createOrderList';
  const tableEl = useRef(null);
  const validationRules = useMemo(() => {
    const rules = {};
    if (isOpportunity) rules.opportunityName = { required: true, minlength: 3 };
    return rules;
  }, [isOpportunity]);
  // Flag to alert if product is digital or not in step 2
  const [isOnlyDigitalProducts, setIsOnlyDigitalProducts] = useState(false);

  const loader = useRef(new Loader('#orderCreateFooter'));
  const loaderProducts = useRef(new Loader('#products'));
  const operatorLoader = useRef(new Loader('#operatorContainer'));
  const customerLoader = useRef(new Loader('#customerContainer'));

  // keeps track of the country and user used to filter the list of variations,
  // if these are not changed when changing operator or shipping address, then we don't need
  // to fetch a fresh list
  const currentVariationFilters = useRef({ country: null, user: null });

  /** Initial State */
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [showRemoveDocument, setShowRemoveDocument] = useModal({
    show: false,
    data: null,
  });
  const [showSendEmailModal, setShowSendEmailModal] = useModal({
    show: false,
    data: null,
  });
  const [isSendOfferModalLoading, setIsSendOfferModalLoading] = useState(false);
  const [checkedTerms, setCheckedTerms] = useState(false);
  // Allow us to get the customer when is from chat and then we are free to select any other team member
  const [getCustomer, setGetCustomer] = useState(customer);
  const [showSelectVariationsModal, setShowSelectVariationsModal] = useModal({
    show: false,
    data: null,
  });
  // Add Product Modal
  const [addProductModal, setAddProductModal] = useModal({
    show: false,
    data: null,
  });
  // Warning Popup
  const [warningModal, setWarningModal] = useModal({
    show: false,
  });

  // region Form Popup
  const [formModal, setFormModal] = useModal({
    show: false,
    data: null,
  });

  const handleFormSave = (productId, formAnswers, formAnswersLink = null) => {
    if (productId) {
      setProducts(prevProducts => {
        const newProducts = prevProducts.map(product => {
          // Check if the product _id matches the specified productId
          if (product._id === productId) {
            // Replace the formAnswerData with the provided formAnswers
            return {
              ...product,
              formAnswerData: formAnswers,
              formAnswersLink,
            };
          }
          return product; // Return unchanged product if _id doesn't match
        });

        tableEl.current.setList(newProducts);
        return newProducts;
      });
    }
    setFormModal({
      show: false,
      data: null,
    });
  };

  // endregion Form Popup

  const [operator, setOperator] = useState(
    getInitialOperator(mode, isOpportunity, opportunity)?.operator,
  );
  const [operatorAddress, setOperatorAddress] = useState(
    getInitialOperator(mode, isOpportunity, opportunity)?.installationAddress,
  );

  const [operatorAddressModal, showOperatorAddressModal] = useModal({
    show: false,
  });
  const [addressModal, showAddressModal] = useModal({
    show: false,
    data: null,
  });

  // We only display the operator block if operators exist for this organization
  // We do an initial request to guarantee there's at least one
  const [hasOperators, setHasOperators] = useState(false);
  const displayOperators = hasOperators && !isOnlyDigitalProducts;
  useEffect(() => {
    let cancelled = false;

    axios
      .get('/user/table/list', {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
        params: {
          isOperator: true,
          page: 0,
          length: 1,
        },
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchOperators');
        return null;
      })
      .then(result => {
        if (!cancelled) {
          setHasOperators(!!result?.data?.data?.length);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleOperatorChange = useCallback(operatorId => {
    operatorLoader.current.show();

    fetchUser(operatorId).then(response => {
      operatorLoader.current.hide();
      setOperator(response?.data || null);
    });
  }, []);

  // Order State, save all the info
  const [order, setOrder] = useState(
    getInitialState(
      mode,
      isOpportunity,
      owner,
      customer,
      productsSelector,
      opportunity,
    ),
  );

  const [shippingProviders, setShippingProviders] = useState([]);
  const productExceededStock = !isOpportunity
    ? !!products.find(p => p.isOutOfStock)
    : false;
  const [isLoading, setIsLoading] = useState(false);

  const enableSubmit =
    !isLoading &&
    checkedTerms &&
    !productExceededStock &&
    order.billingAddress &&
    products.length > 0 &&
    (isOnlyDigitalProducts ? true : shippingProviders.length > 0);
  const [removeModal, showRemoveModal] = useModal({ show: false });

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
    if (loaderProducts.current) {
      if (isLoading) {
        loaderProducts.current.show();
      } else {
        loaderProducts.current.hide();
      }
    }
  }, [isLoading]);
  /** Handle state changes */

  // handle the order changes
  const handleChange = field => value => {
    setOrder(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  // updates the different shipping (address and provider) sent by DisplayShippingContainer component in orderState
  const handleShipping = field => value => {
    setOrder(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleAddingProducts = useCallback(
    (productsList, allProducts = null) => {
      const productsToUpdate =
        productsList?.map(p => {
          const product = p;
          const { userPromotionPrice, userPrice } = product;
          // Get the correct userPrice to use (promo or not)
          product.userPriceToUse =
            userPromotionPrice?.price >= 0 ? userPromotionPrice : userPrice;
          // Quantity by default will be 1
          product.quantityToOrder = product?.quantityToOrder || 1;
          // It is used to identify which products are digital
          product.isDigitalContent = product?.parentProduct
            ? product?.parentProduct?.isDigital
            : product?.digitalDocument &&
              product?.digitalDocument?.url?.length > 0;
          return product;
        }) || [];
      if (!allProducts) {
        // If popup sends products, update the state
        if (productsList) {
          setProducts(() => {
            // updates list with new products + previous products
            tableEl.current.setList([...productsToUpdate]);
            return productsToUpdate;
          });
        }
      } else {
        setWarningModal({
          show: true,
          data: { previous: allProducts, new: productsList },
        });
      }

      // closes popup
      setAddProductModal({
        show: false,
        data: null,
      });
    },
    [tableEl?.current, setAddProductModal, setProducts, products],
  );

  /**
   * Handles Click on Add New Product
   * Does validation to get user first and to make sure we have address selected
   * Opens popup
   * @type {(function(): void)|*}
   */
  const handleAddProduct = useCallback(() => {
    if (!order.seller) {
      Notifications.showNotificationError(
        t('error'),
        t('order.create.errorMsg.selectCustomerFirst'),
      );
      return;
    }
    // user don't have phone number && not have in address already saved one
    if (
      (!order?.seller?.phone || order.seller?.phone?.length === 0) &&
      (!order?.seller?.address?.phone ||
        order?.seller?.address?.phone?.length === 0)
    ) {
      Notifications.showNotificationError(
        t('error'),
        t('marketplace.shippingTab.error.phone'),
      );
      return;
    }
    setAddProductModal({ show: true });
  }, [order?.seller]);

  const getStockForVariations = productsInCheckout => {
    const stocksForCheckout = {};

    // eslint-disable-next-line no-unused-expressions
    productsInCheckout?.forEach(product => {
      const { isPack, packVariations } = product;

      if (isPack) {
        packVariations?.forEach(({ _id: key, stockQuantity, quantity }) => {
          const amount = quantity * product?.quantityToOrder;

          stocksForCheckout[key] = {
            stockQuantity,
            quantityInCheckout:
              (stocksForCheckout[key]?.quantityInCheckout || 0) + amount,
          };
        });
      } else {
        const key = product?._id;
        const stockQuantity = product?.stockQuantity;
        const amount = product?.quantityToOrder;

        stocksForCheckout[key] = {
          stockQuantity,
          quantityInCheckout:
            (stocksForCheckout[key]?.quantityInCheckout || 0) + amount,
        };
      }
    });
    return stocksForCheckout;
  };

  useEffect(() => {
    if (products.length > 0) {
      // Filter by the digital ones
      const hasDigitalInCart = products.filter(
        p => p.isDigitalContent === true,
      );
      // Check if all is digital by comparing the products cart length with the actually digital products in cart
      setIsOnlyDigitalProducts(hasDigitalInCart.length === products.length);
    }
  }, [products]);

  const shippingProviderIsValid = () => {
    let valid = false;
    // New array with valid shipping or undefined attr
    const shipping = shippingProviders.map(
      s =>
        (s.userPrice && s.userPrice.price) ||
        (s.userClickAndCollectPrice && s.userClickAndCollectPrice.price),
    );
    // if there is a shipping different of undefined, the prop will change to valid and the
    // shipping providers list will be rendered
    if (shipping.some(s => s !== undefined)) {
      valid = true;
    }
    return valid;
  };
  /** API REQUESTS */
  // POST - Calculate a shipping provider
  const calculateShippingProvider = (payer, receiver) => {
    // by default the receiver is the payer
    const localReceiver = receiver || payer;

    const params = {
      billingAddress: { ...order.billingAddress, NIF: undefined },
      shippingAddress: {
        ...(operatorAddress || order?.shippingAddress || order.billingAddress),
        NIF: undefined,
      },
      products: getProductsForOrderCreate(products),
      discount: order.discount,
      isPaidWithCoins: order.isPaidWithCoins,
    };

    let url = '/api/market/shipping-provider/calculate-shipping?';
    let country;
    if (
      !localReceiver?.personalInfo?.businessType ||
      localReceiver?.personalInfo?.businessType === 'individual'
    ) {
      country =
        localReceiver.shippingCountry || localReceiver?.address?.countryCode;
    } else {
      country =
        order.billingAddress?.countryCode ||
        localReceiver?.address?.countryCode ||
        localReceiver.shippingCountry;
    }
    if (country) {
      url += `&country=${country}`;
    }
    if (payer.currency) {
      url += `&currency=${payer.currency}`;
    }
    axios
      .post(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setShippingProviders(response.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    let shouldCalculateShipping = true;

    const shippingAddress = operatorAddress || order.shippingAddress;
    const receiver = operator || order.seller;

    // Check the business type to see if need to check the shipping country
    if (
      !receiver?.personalInfo?.businessType ||
      receiver?.personalInfo.businessType === 'individual'
    ) {
      shouldCalculateShipping = receiver?.shippingCountry
        ? receiver?.shippingCountry === shippingAddress?.countryCode ||
          receiver?.shippingCountry === order?.billingAddress?.countryCode
        : true;
    }

    if (!isOnlyDigitalProducts) {
      if (
        order.billingAddress &&
        shouldCalculateShipping &&
        products.length > 0
      ) {
        calculateShippingProvider(order.seller, receiver);
      } else {
        setShippingProviders([]);
      }
    }
  }, [
    order.billingAddress,
    order.shippingAddress,
    operator,
    operatorAddress,
    products.length,
    products,
    isOnlyDigitalProducts,
  ]);

  // State for checkbox, select a different shipping address
  const [shippingAddressIsDifferent, setShippingAddressIsDifferent] = useState(
    getInitialShipping(mode, order),
  );

  /** CHECK WITH THE API THE CORRECT VALUES TO DISPLAY IN ORDER */
  const [orderDetailPrices, setOrderDetailPrices] =
    useState(initialTotalsState);
  /** API REQUESTS */
  const getVariationList = (
    payer,
    receiver,
    selectedShippingAddress = null,
  ) => {
    // by default the receiver is the payer
    const localReceiver = receiver || payer;

    const url = '/api/market/product/available/variation/list';
    let country;
    // First check if there is shipping country
    if (localReceiver.shippingCountry) {
      country = localReceiver.shippingCountry;
    } else {
      // check if a shipping address was chosen, if not use billing address
      country = selectedShippingAddress
        ? selectedShippingAddress.countryCode
        : localReceiver.address.countryCode;
    }

    if (
      country === currentVariationFilters.current.country &&
      payer?._id === currentVariationFilters.current.user
    ) {
      return;
    }

    currentVariationFilters.current.country = country;
    currentVariationFilters.current.user = payer?._id || null;

    const params = {
      isPublic: true,
      country,
      currency: payer?.currency || 'eur',
      user: payer?._id,
    };

    setIsLoading(true);
    axios
      .get(url, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        setIsLoading(false);
        setProductsSelector(response.data);

        // Reset state and list
        setProducts(getInitialProductState(mode, isOpportunity, opportunity));
        setOrderDetailPrices(initialTotalsState);
        tableEl.current.setList(
          getInitialProductState(mode, isOpportunity, opportunity),
        );
      })
      .catch(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(err, 'error_fetchProducts');
        console.log(err);
      });
  };

  const handleDifferentShippingAddress = () => {
    setShippingAddressIsDifferent(prevState => {
      if (prevState) {
        if (!operator) {
          getVariationList(order.seller);
        }
        // In case of turning to false clean shippingAddress
        setOrder(prevOrder => ({ ...prevOrder, shippingAddress: null }));
      }
      return !prevState;
    });
  };
  useEffect(() => {
    if (
      (operator && operatorAddress) ||
      (order.seller && order.shippingAddress)
    ) {
      getVariationList(
        order.seller,
        operator,
        operatorAddress || order.shippingAddress,
      );
    }
  }, [
    order.shippingAddress,
    order.seller,
    operator,
    operatorAddress,
    shippingAddressIsDifferent,
  ]);

  // API Request to get user info;
  const getUser = (customerFromChat = false) => {
    customerLoader.current.show();
    fetchUser(customerFromChat ? customer._id : order?.seller).then(
      response => {
        customerLoader.current.hide();
        if (response) {
          setOrder(prevState => ({
            ...prevState,
            discount: null,
            seller: response.data,
            billingAddress: response.data.address,
            shippingAddress: null,
            shipping: null,
            currency: response.data.currency ? response.data.currency : 'eur',
          }));
          setShippingAddressIsDifferent(false);
          // Get the variations list if the user has a valid billing address
          if (response.data?.address?.countryCode) {
            getVariationList(response.data, operator, operatorAddress);
          }
        }
      },
    );
  };

  // use effect responsible for getting the user selected
  useEffect(() => {
    if (order?.seller && typeof order?.seller === 'string') {
      getUser();
    } else if (getCustomer) {
      setGetCustomer(false);
      getUser(true);
    }
  }, [order?.seller]);

  // CONFIRM PAYMENT
  const confirmPayment = orderId => {
    if (paymentProviderType === PAYMENT_PROVIDER.STRIPE) {
      axios
        .post(
          `/api/payment/intent/market/order/${orderId}/confirm`,
          {},
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'License',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(() => {
          window.location.href = pageInformation.goBackUrl;
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err, 'order_create_fail');
          console.log(err);
        });
    } else {
      window.location.href = pageInformation.goBackUrl;
    }
  };
  // POST AN ORDER
  const createOrder = sendEmail => {
    // Checks if the product list is empty
    if (products.length === 0) {
      Notifications.showErrorFromRequest(null, 'order_create_fail_no_products');
      return;
    }
    // Checks if there is a billing address
    if (order.billingAddress === null) {
      Notifications.showErrorFromRequest(
        null,
        'order_create_fail_no_billingAddress',
      );
      return;
    }
    const billingAddress = handleBillingAddressInOrder(
      order.seller,
      order.billingAddress,
    );
    const { businessType = null, companyName = null } =
      order.seller?.personalInfo || {};
    if (!(businessType === 'individual' || !businessType) && companyName) {
      billingAddress.companyName = companyName;
    }

    const customerShippingAddr = handleShippingAddressInOrder(
      order.seller,
      order?.shippingAddress,
    );
    const operatorShippingAddr = handleShippingAddressInOrder(
      operator,
      operatorAddress,
    );

    const receiver = operator || order.seller;
    const shippingAddress =
      operatorShippingAddr || customerShippingAddr || billingAddress;
    const params = {
      owner: order.seller._id,
      discount: order.discount,
      isPaidWithCoins: order.isPaidWithCoins,
      shipping: order?.shipping?._id || null,
      currency: order.currency,
      // NIFs are added to the addresses for display purposes only
      // and need to be removed to pass the validator
      billingAddress: { ...billingAddress, NIF: undefined },
      shippingAddress: shippingAddress
        ? {
            ...shippingAddress,
            NIF: undefined,
          }
        : undefined,
      installationAddress: operatorShippingAddr
        ? { ...(customerShippingAddr || billingAddress), NIF: undefined }
        : undefined,
      operator: operator?._id,
      products: getProductsForOrderCreate(products, isOpportunity),
      adminNotes: order?.adminNotes || undefined,
      note: order?.note || undefined,
    };
    if (isOpportunity) {
      params.name = order.name;
      params.documents = order?.documents || [];
      params.status = order.status || OPPORTUNITIES_STATUS.PENDING;
      const formsNotValid = notValidProductForms(products);
      if (formsNotValid) {
        Notifications.showNotificationError(
          t('error'),
          t('form.error.not.filled', formsNotValid),
        );
        return;
      }
    }
    const hasProductWithFrequency = products?.find(p => p.frequency);
    if (hasProductWithFrequency?.frequency !== 'only_once') {
      params.frequency = hasProductWithFrequency?.frequency || null; // TODO: CHANGE WHEN MANAGING SUBSCRIPTIONS IN OPPORTUNITY
    }
    let country;
    if (
      !receiver?.personalInfo?.businessType ||
      receiver?.personalInfo?.businessType === 'individual'
    ) {
      country = receiver.shippingCountry || receiver?.address?.countryCode;
    } else {
      country =
        order.billingAddress?.countryCode ||
        receiver?.address?.countryCode ||
        receiver.shippingCountry;
    }
    let url = `${pageInformation.createEndpoint}sendEmail=${sendEmail}`;
    if (country) {
      url += `&country=${country}`;
    }
    if (order.seller.currency) {
      url += `&currency=${order.seller.currency}`;
    }

    if (
      orderDetailPrices.total === 0 ||
      paymentProviderType === PAYMENT_PROVIDER.STRIPE
    ) {
      params.paymentMethodType = 'bank_transfer_manual';
    }

    if (isOpportunity && isEdit) {
      params.signature = null;
    }

    let errorPackProducts = {
      hasError: false,
    };
    const advancedPacks = products?.filter(p => p.packType === 'advanced');
    advancedPacks?.forEach(pack => {
      const { packVariations, amountVariationsPerAdvancedPack } = pack || {};
      const numberOfProducts =
        packVariations?.reduce(
          (acc, item) => acc + parseInt(item.quantity, 10),
          0,
        ) || 0;
      if (amountVariationsPerAdvancedPack !== numberOfProducts) {
        errorPackProducts = {
          hasError: true,
          amountVariationsPerAdvancedPack,
        };
      }
    });
    if (errorPackProducts?.hasError) {
      Notifications.showNotificationWarning(
        t('warning'),
        t(
          'advancedPack.error.amountVariationsPerAdvancedPack',
          errorPackProducts?.amountVariationsPerAdvancedPack,
        ),
      );
      setIsSendOfferModalLoading(false);
      setShowSendEmailModal({ show: false, data: null });
      return;
    }

    setIsLoading(true);
    axios[isEdit ? 'put' : 'post'](url, params, {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Games',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    })
      .then(({ data: response }) => {
        const orderId = response.data._id;
        let successMessage = t('order_create_success');

        if (isOpportunity) {
          successMessage = sendEmail
            ? t('send.offer.email.sent')
            : t('send.offer.email.not.sent');
        }
        Notifications.showNotificationSuccess(t('success'), successMessage);
        if (
          !isOpportunity &&
          orderId &&
          (response.data.total === 0 ||
            paymentProviderType === PAYMENT_PROVIDER.STRIPE)
        ) {
          let requestURL = `/api/payment/intent/market/order/${orderId}/capture`;

          if (paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
            requestURL = `/api/order/market/${orderId}/capture_payment`;
          }
          axios
            .post(
              requestURL,
              {
                paymentMethod: 'bank_transfer_manual',
              },
              {
                headers: {
                  'Accept-Version': '1.0.0',
                  'X-Gamification-API': 'License',
                  'X-CSRF-Token': _csrf,
                  _csrf,
                },
              },
            )
            .then(() => {
              if (response.data.total === 0) {
                confirmPayment(orderId);
              } else {
                window.location.href = pageInformation.goBackUrl;
              }
            })
            .catch(err => {
              Notifications.showErrorFromRequest(err, 'order_create_fail');
              console.log(err);
            });
        } else {
          window.location.href = pageInformation.goBackUrl;
        }

        setIsLoading(false);
      })
      .catch(error => {
        Notifications.showErrorFromRequest(error, 'order_create_fail');
        setIsLoading(false);
      });
  };

  const openEmailPopup = useCallback(() => {
    setShowSendEmailModal({ show: true, data: null });
  }, []);

  const handleCreateOpportunity = useCallback(
    async (sendEmail, type) => {
      if (type === 'isCancel') {
        setShowSendEmailModal({ show: false, data: null });
        return;
      }
      setIsSendOfferModalLoading(true);
      const formsNotValid = notValidProductForms(products);
      if (formsNotValid) {
        Notifications.showNotificationError(
          t('error'),
          t('form.error.not.filled', formsNotValid),
        );
        setShowSendEmailModal({ show: false, data: null });
        return;
      }
      await createOrder(sendEmail);
    },
    [mode, setShowSendEmailModal, order, setIsSendOfferModalLoading, products],
  );

  const checkOrderDetail = (payer, receiver) => {
    // by default the receiver is the payer
    const localReceiver = payer || receiver;

    const params = {
      products: getProductsForOrderCreate(products),
      owner: order.seller._id,
      discount: order.discount,
      isPaidWithCoins: order.isPaidWithCoins,
    };
    if (order.billingAddress) {
      params.billingAddress = { ...order.billingAddress, NIF: undefined };
    }

    const shippingAddress = operatorAddress || order.shippingAddress;
    if (shippingAddress && shippingAddress.countryCode) {
      params.shippingAddress = { ...shippingAddress, NIF: undefined };
    } else if (params.billingAddress) {
      params.shippingAddress = params.billingAddress;
    }

    if (order.shipping) {
      params.shipping = order.shipping._id;
    }
    let country;
    if (
      !localReceiver?.personalInfo?.businessType ||
      localReceiver?.personalInfo?.businessType === 'individual'
    ) {
      country =
        localReceiver.shippingCountry || localReceiver?.address?.countryCode;
    } else {
      country =
        order.billingAddress?.countryCode ||
        localReceiver?.address?.countryCode ||
        localReceiver.shippingCountry;
    }
    let url = pageInformation.calculateEndpoint;
    if (country) {
      url += `&country=${country}`;
    }
    if (payer.currency) {
      url += `&currency=${payer.currency}`;
    }
    axios
      .post(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        // Destructing response
        const { data } = response;
        const {
          pricesWithoutVAT,
          subtotal,
          userDiscountTotal,
          userDiscount,
          total,
          subtotalTax,
          shippingTotal,
          shippingTax,
          totalVA,
          totalCV,
          subtotalExtraTax,
        } = data;

        setOrderDetailPrices({
          subtotal,
          userDiscountPercentage: userDiscount,
          userDiscount: userDiscountTotal,
          total,
          tax: subtotalTax,
          pricesWithoutVAT,
          shippingTotal,
          shippingTax,
          shippingSubTotalTax: data.shippingSubTotalTax,
          shippingSubTotal: data.shippingSubTotal,
          totalVA,
          totalCV,
          currency: payer.currency,
          country,
          subtotalExtraTax,
          discountTotal: data.discountTotal,
          coinsDiscountTotal: data.coinsDiscountTotal || 0,
          shippingDiscountTotal: data.shippingDiscountTotal || 0,
        });
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchProductsList');
        console.log(err);
      });
  };
  useEffect(() => {
    // we need to make sure that it's only checking if there are products in the state
    if (products.length > 0) {
      checkOrderDetail(order.seller, operator);
    }
  }, [
    products,
    operator,
    operatorAddress,
    order.seller,
    order.billingAddress,
    order.shippingAddress,
    order.shipping,
    order.discount,
    order.isPaidWithCoins,
  ]);

  const hasAddress =
    (operator && operatorAddress) ||
    (order.seller && (order?.shippingAddress || order.billingAddress));

  /** RENDER COLUMNS - List */
  function handleListInitialization() {
    const $tableSelector = $(`#${tableId}`);
    $('period_').select2();
    $tableSelector.on('click', '.remove-product-var', function edit() {
      const indexEvent = $(this).attr('data-product-variation-index');
      showRemoveModal({ show: true, index: indexEvent });
    });

    $tableSelector.on('change keyup', '.quantity-input', function (event) {
      event.preventDefault(); // Prevent the default behavior of the input event
      const $input = $(this); // Cache $(this) as $input for later use
      const newValue = parseInt($input.val(), 10);
      const id = $input.data('id'); // Use .data() method to get the 'data-id' attribute value

      clearTimeout($input.data('timeout')); // Clear previous timeout associated with this input

      const inputTimeout = setTimeout(() => {
        setProducts(prevState => {
          const newProductQuantity = prevState.map(item => {
            // If the item's _id matches the target id, update its quantityToOrder
            if (item._id === id) {
              return {
                ...item,
                quantityToOrder: newValue, // Replace newValue with the desired quantity
              };
            }
            // If it's not the target item, return the item unchanged
            return item;
          });
          const stocksForCheckout = getStockForVariations(newProductQuantity);
          tableEl.current.setList([
            ...newProductQuantity.map(product => {
              const newProduct = product;
              const { isPack, packVariations } = newProduct;

              if (isPack) {
                const variationOutOfStock = packVariations?.find(
                  v =>
                    stocksForCheckout[v?._id].stockQuantity &&
                    stocksForCheckout[v?._id].stockQuantity <
                      stocksForCheckout[v?._id].quantityInCheckout,
                );
                newProduct.isOutOfStock = !!variationOutOfStock;
              } else {
                newProduct.isOutOfStock =
                  stocksForCheckout[newProduct._id].stockQuantity &&
                  stocksForCheckout[newProduct._id].stockQuantity <
                    stocksForCheckout[newProduct._id].quantityInCheckout;
              }
              return newProduct;
            }),
          ]);
          return newProductQuantity;
        });
      }, 300);

      $input.data('timeout', inputTimeout); // Store the new timeout associated with this input
    });

    $tableSelector.on('change', '.period-input', function (event) {
      event.preventDefault(); // Prevent the default behavior of the input event
      const $input = $(this); // Cache $(this) as $input for later use
      const newValue = $input.val();
      const id = $input.data('id'); // Use .data() method to get the 'data-id' attribute value

      const previousFrequency = products.find(p => p._id)?.frequency;
      const productsToChange = products.map(p => {
        const updatedProduct = p;
        if (updatedProduct._id === id) {
          updatedProduct.frequency = newValue;
        }
        return updatedProduct;
      });
      // If user is trying to select a subscription, and we have more that one product on the list
      // it shouldn't be possible
      if (newValue !== 'only_once' && products.length > 1) {
        setWarningModal({ show: true, data: productsToChange });
        $(this).val(previousFrequency);
      } else {
        const itemsMatched = products?.filter(
          p =>
            !p?.parentProduct?.availableForSubscription ||
            p.parentProduct?.availableSubscriptionFrequency?.includes(newValue),
        );
        // If we are trying to select only_once but some product's don't have it! It shouldn't be possible
        if (itemsMatched?.length !== products.length) {
          setWarningModal({ show: true, data: productsToChange });
          $(this).val(previousFrequency);
        } else {
          // success case, update selector and state
          setProducts(prevState => {
            const newProductQuantity = prevState.map(item => {
              // If the item's _id matches the target id, update its quantityToOrder
              if (item._id === id) {
                return {
                  ...item,
                  frequency: newValue, // Replace newValue with the desired quantity
                };
              }
              // If it's not the target item, return the item unchanged
              return item;
            });
            tableEl.current.setList([...newProductQuantity]);
            return newProductQuantity;
          });
          $input.val(newValue);
        }
      }
    });

    $tableSelector.on('click', '.btn-fill-form', function () {
      const $btn = $(this);
      const productId = $btn.data('product');

      const product = products.find(p => p._id === productId);

      const isSurveyJSForm =
        product?.form?.providerType === PROVIDER_TYPE.SURVEY_JS;
      const formToModal = isSurveyJSForm
        ? {
            providerType: product?.form?.providerType,
            productId,
            formQuestionData: product?.form?.providerData,
            formAnswerData: product?.formAnswerData || null,
          }
        : {
            providerType: product?.form?.providerType,
            productId,
            formAnswerData: product?.formAnswerData || null,
            templatePDF: product?.form?.templatePDF,
            productForm: product?.form,
            providerData: isEdit
              ? {
                  ...product?.form?.providerData,
                  formFieldValues:
                    product?.formAnswerData?.formFieldValues ||
                    product?.form?.providerData?.formFieldValues,
                }
              : product?.form?.providerData,
            formAnswersLink: product?.formAnswersLink,
          };
      setFormModal({
        show: true,
        data: formToModal,
      });
    });

    $tableSelector.on('click', '.btn-select-products', function () {
      const pack = $(this).attr('data-pack');
      setShowSelectVariationsModal({
        show: true,
        data: pack ? JSON.parse(pack) : null,
      });
    });

    $tableSelector.on('click', '.btn-products-inside', function () {
      const pack = $(this).attr('data-pack');
      setShowSelectVariationsModal({
        show: true,
        data: pack ? JSON.parse(pack) : null,
      });
    });
  }

  const handlePackProducts = useCallback(
    (productId, packVariations) => {
      setProducts(prevProducts => {
        const newProducts = prevProducts?.map(product => {
          if (product?._id === productId) {
            return { ...product, packVariations };
          }
          return product;
        });
        tableEl.current.setList(newProducts);
        return newProducts;
      });
    },
    [setProducts],
  );
  const handleClosePackModal = useCallback(
    (productId, packVariations) => {
      setShowSelectVariationsModal({ show: false, data: null });
      if (packVariations?.length > 0) {
        handlePackProducts(productId, packVariations);
      }
    },
    [setShowSelectVariationsModal, handlePackProducts],
  );
  const handleRemoveModalClose = () => {
    showRemoveModal({ show: false, index: null });
  };
  const handleRemove = index => {
    showRemoveModal({ show: false, index: null });
    tableEl.current.removeListItem(index);
    setProducts(prevState => {
      prevState.splice(index, 1);
      // Filter by the digital ones
      const hasDigitalInCart = prevState.filter(
        p => p.isDigitalContent === true,
      );
      // Check if all is digital by comparing the products cart length with the actually digital products in cart
      setIsOnlyDigitalProducts(hasDigitalInCart.length === prevState.length);
      return prevState;
    });
  };

  useEffect(() => {
    if (isOnlyDigitalProducts) {
      setOrder(prevState => ({
        ...prevState,
        shipping: null,
        shippingAddress: null,
      }));
    }
  }, [isOnlyDigitalProducts]);

  /** region Handle Pop ups */
  const handleAddUserClose = useCallback(
    createdUser => {
      if (createdUser) handleChange('seller')(createdUser._id);
    },
    [handleChange],
  );
  const handleAddressOpen = useCallback(
    (type, address, index) => {
      showAddressModal({ show: true, data: { address, type, index } });
    },
    [showAddressModal],
  );

  const handleAddOperatorAddress = useCallback(
    (type, address, index) => {
      showOperatorAddressModal({
        show: true,
        data: { address, type: index === 0 ? 'billing' : 'shipping', index },
      });
    },
    [showAddressModal],
  );

  const updateUserInformation = useCallback(
    async (user, additionalInformation, addressToSave) => {
      try {
        if (addressToSave === null) {
          showAddressModal({ show: false, data: null });
          return;
        }
        const { type, index } = additionalInformation?.data;
        const params = {};
        if (type === 'billing') {
          params.address = validateAddressFields(addressToSave);
          if (addressToSave?.phone) {
            params.phone = addressToSave?.phone;
          }
        }
        if (type === 'shipping') {
          const fullShippingAddresses = user?.shippingAddress;
          if (typeof index === 'number') {
            fullShippingAddresses[index] = validateAddressFields(addressToSave);
            params.shippingAddress = fullShippingAddresses;
          } else {
            params.shippingAddress = [
              ...fullShippingAddresses,
              validateAddressFields(addressToSave),
            ];
          }
        }
        const { data: response } = await axios.put(
          `/api/user/${user._id}/settings`,
          params,
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Organization',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        );
        // eslint-disable-next-line consistent-return
        return {
          userUpdated: response.data,
          changeType: type,
          address: addressToSave,
        };
      } catch (err) {
        console.error(err);
        Notifications.showErrorFromRequest(err);
        // eslint-disable-next-line consistent-return
        throw err;
      }
    },
    [],
  );

  const handleCloseOperatorAddressModal = useCallback(
    async addressToSave => {
      if (!addressToSave) {
        showOperatorAddressModal({ show: false, data: null });
        return;
      }
      operatorLoader.current.show();
      const { userUpdated } = await updateUserInformation(
        operator,
        operatorAddressModal,
        addressToSave,
      );
      setOperator(userUpdated);
      operatorLoader.current.hide();
      showOperatorAddressModal({ show: false, data: null });
    },
    [operatorAddressModal?.data, _csrf, operator?._id],
  );

  const handleAddressClose = useCallback(
    async addressToSave => {
      if (!addressToSave) {
        showAddressModal({ show: false, data: null });
        return;
      }
      const { userUpdated, changeType, address } = await updateUserInformation(
        order.seller,
        addressModal,
        addressToSave,
      );
      setOrder(prevState => ({
        ...prevState,
        billingAddress: userUpdated.address || prevState.billingAddress,
        shippingAddress:
          changeType === 'shipping' ? address : prevState.billingAddress,
        phone: userUpdated.phone || prevState.phone,
        seller: userUpdated,
      }));
      showAddressModal({ show: false, data: null });
    },
    [addressModal?.data, _csrf, order?.seller?._id, setOrder, showAddressModal],
  );
  const handleWarningModalClose = useCallback(
    (save, productsConfirmed) => {
      if (productsConfirmed?.new && productsConfirmed?.previous) {
        if (save) {
          setProducts(productsConfirmed?.new);
          tableEl.current.setList(productsConfirmed?.new);
        } else {
          setProducts(productsConfirmed?.previous);
          tableEl.current.setList(productsConfirmed?.previous);
        }
        setWarningModal({ show: false, data: null });
        return;
      }
      if (save) {
        const validatedProducts =
          handleValidationFrequencies(productsConfirmed);
        setProducts(validatedProducts);
        tableEl.current.setList(validatedProducts);
      }
      setWarningModal({ show: false, data: null });
    },
    [setWarningModal, products, handleValidationFrequencies],
  );
  /** endregion Handle Pop ups */

  const handleGoBack = useCallback(() => {
    window.location.href = pageInformation.goBackUrl;
  }, [isOpportunity]);

  const columns = useMemo(() => {
    const result = [
      {
        field: 'Name',
        width: 250,
        textAlign: 'left',
        title: t('order_product_name'),
        sortable: false,
        overflow: 'visible',
        template: item =>
          renderToStaticMarkup(<ProductItem t={t} item={item} />),
      },
      {
        field: 'quantity',
        width: 80,
        textAlign: 'left',
        title: t('order_product_quantity'),
        sortable: false,
        overflow: 'visible',
        template: variation =>
          renderToStaticMarkup(
            <input
              min={1}
              className="form-control m-input quantity-input"
              id={`quantity_${variation._id}`}
              name={`quantity_${variation._id}`}
              type="number"
              value={variation.quantityToOrder}
              data-id={variation._id}
              data-stockQuantity={variation.stockQuantity}
              data-quantityToOrder={variation.quantityToOrder || 1}
            />,
          ),
      },
      {
        field: 'period',
        width: 180,
        textAlign: 'left',
        title: t('period'),
        sortable: false,
        overflow: 'visible',
        template: variation =>
          renderToStaticMarkup(
            <>
              {variation?.parentProduct?.availableForSubscription ? (
                <select
                  className="form-control m-input period-input"
                  id={`period_${variation._id}`}
                  name={`period_${variation._id}`}
                  data-id={variation._id}
                >
                  {variation?.parentProduct?.availableSubscriptionFrequency?.map(
                    frequency => (
                      <option
                        value={frequency}
                        selected={frequency === variation?.frequency}
                      >
                        {t(`product.subscription.frequency.${frequency}`)}
                      </option>
                    ),
                  )}
                </select>
              ) : (
                '-'
              )}
            </>,
          ),
      },
    ];

    if (isOpportunity) {
      result.push({
        field: 'form',
        title: t('form'),
        textAlign: 'left',
        width: 100,
        template: ({ form }) =>
          renderToStaticMarkup(
            <span className="form-text">{form?.name || '-'}</span>,
          ),
      });
    }

    result.push(
      {
        field: 'unitPrice',
        width: 80,
        textAlign: 'left',
        title: t('order_unit_price'),
        sortable: false,
        overflow: 'visible',
        template: ({ userPriceToUse }) =>
          renderToStaticMarkup(
            <>
              {formatPrice(
                userPriceToUse.price,
                findCurrencySymbol(userPriceToUse.code, currencies),
              )}
            </>,
          ),
      },
      {
        field: 'vat',
        title: t('settings.configuration.modules.marketplace.tax'),
        textAlign: 'left',
        width: 50,
        template: ({ userPriceToUse }) =>
          renderToStaticMarkup(<>{`${userPriceToUse?.tax}%`}</>),
      },
    );

    if (showVA) {
      result.push({
        field: 'va',
        title: t('commissions.va'),
        textAlign: 'left',
        width: 60,
        template: ({ va, quantityToOrder, commissionable }) =>
          renderToStaticMarkup(
            <>
              {va && commissionable
                ? normalizeNumber(va * (quantityToOrder || 1), 2)
                : '-'}
            </>,
          ),
      });
    }

    if (showCV) {
      result.push({
        field: 'cv',
        title: t('commissions.cv'),
        textAlign: 'left',
        width: 60,
        template: ({ cv, quantityToOrder, commissionable }) =>
          renderToStaticMarkup(
            <>
              {cv && commissionable
                ? normalizeNumber(cv * (quantityToOrder || 1), 2)
                : '-'}
            </>,
          ),
      });
    }
    result.push({
      field: 'total',
      title: t('order_item_subtotal'),
      textAlign: 'left',
      width: 60,
      template: ({ userPriceToUse, quantityToOrder }) =>
        renderToStaticMarkup(
          <div className="price-unit_column">
            {formatPrice(
              userPriceToUse.price * (quantityToOrder || 1),
              findCurrencySymbol(userPriceToUse.code, currencies),
            )}
          </div>,
        ),
    });

    if (isOpportunity) {
      result.push({
        field: 'formBtn',
        title: '',
        textAlign: 'left',
        width: 150,
        template: product =>
          renderToStaticMarkup(
            product?.form ? <FormButton product={product} t={t} /> : null,
          ),
      });
    }

    result.push({
      field: 'actions',
      title: '',
      textAlign: 'left',
      width: 30,
      template: (item, index) =>
        renderToStaticMarkup(
          <button
            type="button"
            className="remove-product-var action-remove-style"
            data-product-variation-index={index}
          >
            <i className="fa fa-trash" />
          </button>,
        ),
    });

    return result;
  }, []);

  /** region Add Documents by updating the opportunity */
  const onAddDocument = useCallback(
    async params => {
      setIsLoadingBtn(true);
      setOrder(prevOrder => ({
        ...prevOrder,
        ...params,
      }));
      setIsLoadingBtn(false);
    },
    [order],
  );
  /** endregion Add Documents by updating the opportunity */

  /** region remove document */
  const onRemoveDocument = useCallback(
    async params => {
      setShowRemoveDocument({ show: true, data: params });
    },
    [setShowRemoveDocument],
  );
  const onClosePopup = useCallback(
    () => setShowRemoveDocument({ show: false, data: null }),
    [setShowRemoveDocument],
  );

  const removeDocument = useCallback(async () => {
    setIsLoadingModal(true);
    setOrder(prevOrder => ({
      ...prevOrder,
      documents: prevOrder?.documents?.filter(
        doc => doc.url !== showRemoveDocument?.data?._id,
      ),
    }));
    setShowRemoveDocument({ show: false, data: null });
    setIsLoadingModal(false);
  }, [order?.documents, showRemoveDocument]);
  /** endregion remove document */

  const handleCouponSelection = useCallback(coupon => {
    setOrder(prevOrder => ({
      ...prevOrder,
      discount: prevOrder?.discount === coupon ? null : coupon,
    }));
  }, []);

  const handleCoinsClick = isPaidWithCoins => {
    setOrder(prevOrder => ({
      ...prevOrder,
      isPaidWithCoins,
    }));
  };
  /** Render Component */
  return (
    <>
      {showSelectVariationsModal?.show && (
        <AddPackProduct
          product={{
            ...showSelectVariationsModal?.data,
            pack: {
              amountVariationsPerAdvancedPack:
                showSelectVariationsModal?.data
                  ?.amountVariationsPerAdvancedPack,
            },
          }}
          user={operator || order.seller}
          onClose={packVariations =>
            handleClosePackModal(
              showSelectVariationsModal?.data?._id,
              packVariations,
            )
          }
          selectedVariations={
            showSelectVariationsModal?.data?.packVariations || []
          }
        />
      )}
      {showSendEmailModal.show ? (
        <BasicModal
          onClose={(save, data, type) =>
            handleCreateOpportunity(save, data, type)
          }
          id="showSendEmailModal"
          title={t('send.offer.email')}
          saveMessage={t('yes')}
          cancelMessage={t('no')}
          showCloseOnTop
          isLoading={isSendOfferModalLoading}
          disabled={isSendOfferModalLoading}
        >
          <span>{t('send.offer.email.question')}</span>
        </BasicModal>
      ) : null}
      {showRemoveDocument?.show ? (
        <RemoveDocumentModal
          isLoading={isLoadingModal}
          onClose={onClosePopup}
          onUpdate={removeDocument}
          documentName={showRemoveDocument?.data?.documents?.originalName}
        />
      ) : null}
      {formModal.show ? (
        <>
          {formModal?.data?.providerType === PROVIDER_TYPE.SURVEY_JS ? (
            <FormModal
              form={formModal.data}
              onClose={(pId, answers) => handleFormSave(pId, answers)}
            />
          ) : (
            <PdfFormModal
              form={formModal.data}
              onClose={(pId, answers, link) =>
                handleFormSave(pId, answers, link)
              }
              opportunity={order}
              userToFillForm={order.seller}
            />
          )}
        </>
      ) : null}
      {warningModal.show ? (
        <BasicModal
          onClose={handleWarningModalClose}
          id="notAllowedModal"
          saveMessage={t('yes')}
          data={warningModal.data}
        >
          <WarningSection />
        </BasicModal>
      ) : null}
      ›
      {addressModal.show && (
        <AddAddressModal
          vatTypes={vatTypes}
          onClose={handleAddressClose}
          countries={countries}
          userProfile={order?.seller}
          address={addressModal?.data?.address || AddAddressModal.defaultProp}
          isShippingAddress
          index={addressModal?.data?.index}
        />
      )}
      {removeModal.show && (
        <RemoveProductVariationModal
          onRemove={handleRemove}
          index={parseInt(removeModal.index, 10)}
          onClose={handleRemoveModalClose}
        />
      )}
      {operatorAddressModal.show && (
        <AddAddressModal
          vatTypes={vatTypes}
          onClose={handleCloseOperatorAddressModal}
          countries={countries}
          userProfile={operator}
          address={
            operatorAddressModal?.data?.address || AddAddressModal.defaultProp
          }
          isShippingAddress
          index={operatorAddressModal?.data?.index}
        />
      )}
      {addProductModal?.show ? (
        <AddNewProductModal
          onClose={handleAddingProducts}
          payer={order?.seller}
          receiver={operator}
          selectedShippingAddress={operatorAddress || order?.shippingAddress}
          categories={categories}
          catalogTypes={catalogTypes}
          productVariations={products}
          endpoint="/table/market/product/available/list?populate=form&sort=-name"
        />
      ) : null}
      <Title title={pageInformation.title}>
        <ContentContainer>
          <SubHeader title={pageInformation.title} />
          <div className="m-content shop products-toPay-container">
            <div className="m-portlet__body">
              <Form
                id={formId}
                submitMethod={!isOpportunity ? createOrder : openEmailPopup}
                validationRules={validationRules}
              >
                {submitHandler => (
                  <div className="justify-content-center">
                    <CustomerSection
                      order={order}
                      onChange={handleChange}
                      isOnlyDigitalProducts={isOnlyDigitalProducts}
                      onShippingChange={handleShipping}
                      isShippingAddressDifferent={shippingAddressIsDifferent}
                      onDifferentShippingAddress={
                        handleDifferentShippingAddress
                      }
                      userTypeNames={userTypeNames}
                      onCloseAddUser={handleAddUserClose}
                      onAddressOpen={handleAddressOpen}
                      isOpportunity={isOpportunity}
                      languages={languages}
                      mode={mode}
                    />
                    <div id="products" className="col-12 products-container">
                      <div className="">
                        <h4 className="order-card-title">
                          {t('shop.cart.products')}
                        </h4>
                        <div className="col-lg-12 products-mobile">
                          <List
                            id={tableId}
                            ref={tableEl}
                            loading={false}
                            data={[]}
                            columns={columns}
                            onInit={handleListInitialization}
                            pagination={false}
                            onDataUpdated={() => {}}
                          />
                        </div>
                        <div className="col-lg-12 button-add-newProduct">
                          <button
                            type="button"
                            onClick={handleAddProduct}
                            className="btn btn-dynamic m-btn m-btn--icon
                               m-btn--icon-only m-btn--custom m-btn--outline-2x
                               m-btn--pill m-btn--air form"
                          >
                            <i className="la la-plus" />
                            <span style={{ minWidth: '200px' }}>
                              {t('order_add_new_product')}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <DocumentsTab
                      order={{
                        ...order,
                        documents: order?.documents || [],
                      }}
                      onAddDocument={onAddDocument}
                      isLoadingBtn={isLoadingBtn}
                      onRemoveClick={onRemoveDocument}
                      wrapperClass="col-12 opportunity-document-create"
                    />
                    {displayOperators && (
                      <OperatorSection
                        handleAddOperatorAddress={handleAddOperatorAddress}
                        handleAddressOpen={handleAddOperatorAddress}
                        operator={operator}
                        userTypeNames={userTypeNames}
                        handleOperatorChange={handleOperatorChange}
                        setOperatorAddress={setOperatorAddress}
                        languages={languages}
                      />
                    )}
                    {!isOnlyDigitalProducts && (
                      <div className="m-portlet m-portlet--mobile col-lg-12 col-12">
                        <div className="m-portlet__body shippingProvider-wrapper">
                          <h4 className="order-card-title">
                            {t('shipping_providers')}
                          </h4>
                          <div className="create-order-shipping-provider col-12">
                            {shippingProviders.length > 0 &&
                            shippingProviderIsValid() ? (
                              <DisplayShippingContainer
                                shipping={shippingProviders}
                                shippingSelected={order?.shipping?._id || null}
                                isProvider
                                currencies={currencies}
                                onShippingChange={handleShipping('shipping')}
                              />
                            ) : (
                              <span className="customer-info-invalid">
                                {hasAddress && products.length > 0
                                  ? t('order.providers_not_delivery')
                                  : t('order.noShippingProvider')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <OrderCreateFooter
                      isOpportunity={isOpportunity}
                      order={order}
                      products={products}
                      orderDetailPrices={orderDetailPrices}
                      showVA={showVA}
                      showCV={showCV}
                      onCouponSelection={handleCouponSelection}
                      onCoinsSelection={handleCoinsClick}
                      currencies={currencies}
                      enableSubmit={enableSubmit}
                      handleGoBack={handleGoBack}
                      pageInformation={pageInformation}
                      submitHandler={submitHandler}
                      onChange={handleChange}
                      checkedTerms={checkedTerms}
                      onCheckboxChange={() => {
                        setCheckedTerms(prevTerms => !prevTerms);
                      }}
                    />
                  </div>
                )}
              </Form>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

OrderCreate.propTypes = {
  t: PropTypes.func.isRequired,
  owner: PropTypes.string.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  _csrf: PropTypes.string.isRequired,
  customer: PropTypes.shape(userProp.propType),
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  catalogTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
      isActive: PropTypes.bool,
      translations: PropTypes.shape({}),
    }),
  ).isRequired,
  isOpportunity: PropTypes.bool,
  languages: PropTypes.shape(CountryProp.propType).isRequired,
  mode: PropTypes.string,
  opportunity: PropTypes.shape(OrderProp.propType).isRequired,
};

OrderCreate.defaultProps = {
  customer: null,
  isOpportunity: false,
  mode: 'create',
};

export default translate(OrderCreate);
